import { createFileRoute } from "@tanstack/react-router";
import { canonicalLink, pathFromMatch, routeMeta } from "@/lib/seo";
import { serviceHeadScripts } from "@/lib/serviceJsonLd";
import { ServicePage } from "@/components/ServicePage";
import { SeriesBadge } from "@/components/SeriesBadge";
import heroAsset from "@/assets/solair-awning-book-club-deck.jpg.asset.json";
const hero = heroAsset.url;
const heroPicture = heroAsset.url;

export const Route = createFileRoute("/revival-series")({
  head: (ctx) => ({
    links: [canonicalLink(pathFromMatch(ctx))],
    meta: routeMeta(ctx, {
      title: "Revival Series — Awning & Shade Service & Recovery | Texas Made Shade",
      description: "Revival Series is our service and recovery program — storm damage repair, motor replacement, refabric, reweld and full restoration for awnings, retractables, sail shades and covers across Texas.",
      image: hero,
      socialTitle: "Revival Series — Service & Recovery",
      socialDescription: "Storm damage, torn fabric, dead motors or bent frames — we restore shade systems back to full working order.",
      keywords: "awning repair Texas, retractable awning motor replacement, sail shade repair, awning refabric, storm damage awning repair San Antonio, Austin awning service, Seguin awning repair, revival series, awning repair san antonio, awning repair austin, storm damage shade repair texas, retractable awning service new braunfels, awning refabric texas statewide",
    }),
    scripts: serviceHeadScripts({
      path: "/revival-series",
      serviceName: "Revival Series — Service & Recovery",
      breadcrumbName: "Revival Series",
      description: "Service, repair and recovery for awnings, retractables, sail shades, patio curtains and welded frames — including storm damage, motor replacement, refabric and reweld.",
    }),
  }),
  component: () => (
    <ServicePage
      currentPath="/revival-series"
      serviceName="Revival Series"
      eyebrow="REVIVAL SERIES — SERVICE & RECOVERY"
      title="Revival Series — Awning & Shade Service & Recovery"
      intro="Revival Series brings tired, torn or storm-damaged shade back to life. Motor replacement, refabric, reweld, hardware swaps and full system restoration — for our builds and for shade from other manufacturers."
      heroImage={heroPicture}
      preContent={
        <div className="mb-6 flex justify-center">
          <SeriesBadge slug="revival-series" size="md" priority />
        </div>
      }
      heroCta={{
        eyebrow: "Revival Series · Statewide Texas",
        headline: "Bring Your Shade Back to Life.",
        subhead: "Send photos and describe what happened — we'll send back a repair or restoration quote, usually same business day.",
        primaryHref: "/estimate-quotes#general",
        primaryLabel: "Request Revival Quote",
        phoneNumber: "+1 (830) 390-9588",
      }}
      bodyLayout="accordion"
      features={[
        "Storm and wind damage repair",
        "Retractable motor, remote and sensor replacement",
        "Refabric on existing frames (any brand)",
        "Reweld and reinforce bent or cracked aluminum frames",
        "Hardware, bracket and fastener swaps",
        "Full system restoration and re-installation",
      ]}
      applications={[
        "Residential patios, decks and pool areas",
        "Restaurants, storefronts and commercial patios",
        "Schools, churches and municipal shade structures",
        "Boats, RVs, wagons and stock trailers",
        "Farm and ranch equipment",
      ]}
      keywordOutline={[
        { id: "overview", label: "What Revival Series Fixes" },
        { id: "why-us", label: "Why Choose Revival" },
        { id: "details", label: "Repair & Restoration Process" },
        { id: "applications", label: "What We Restore" },
        { id: "faqs", label: "Revival Series FAQs" },
      ]}
      body={[
        {
          heading: "What Revival Series Fixes",
          text: "Anything shade-related that stopped working the way it should. We service builds we made and shade from other manufacturers.",
          bullets: [
            "Torn, faded or mildewed fabric",
            "Dead motors, bad remotes and failed wind sensors",
            "Bent, cracked or corroded frames",
            "Loose or missing hardware and brackets",
            "Storm, wind and hail damage",
          ],
        },
        {
          heading: "Repair or Full Restoration",
          text: "Some jobs are a fabric swap and a motor. Others need a full teardown, frame reweld and re-install. We quote both paths honestly so you can decide.",
          bullets: [
            "On-site inspection with written quote",
            "Refabric using marine-grade Sunbrella® and Herculite®",
            "In-house aluminum and steel welding",
            "Motor, gear and control replacement",
            "Re-install and system test",
          ],
        },
        {
          heading: "Why Choose Revival",
          text: "Thirty-plus years of building custom shade in Texas means we've seen every failure mode. We fix the cause, not just the symptom — so the repair actually holds.",
          bullets: [
            "In-house sewing, welding and installation crew",
            "Marine-grade fabric and hardware only",
            "Works on any brand of awning or shade",
            "Fast turnaround on storm-damage jobs",
          ],
        },
      ]}
    />
  ),
});
